// Einzige Brücke zwischen Stores und Skizzen-UI (C7).
import { useCallback, useEffect } from 'react';
import { useProjectStore } from '../../../stores/projectStore';
import { useAuthStore } from '../../../stores/authStore';
import { useToleranceStore } from '../../../stores/toleranceStore';
import { toast } from '../../../lib/toast';
import { sketchSlot } from './sketchView';
import { imageFromPaste } from './pasteImage';

export function useSketch() {
  const calcId = useProjectStore((s) => s.currentCalculation?.id || null);
  const sketchUrl = useProjectStore((s) => s.currentCalculation?.sketch_url || null);
  const uploading = useProjectStore((s) => s.uploadingSketch);
  const uploadSketch = useProjectStore((s) => s.uploadSketch);
  const removeSketch = useProjectStore((s) => s.removeSketch);
  const plan = useAuthStore((s) => s.profile?.plan);
  const preview = useToleranceStore((s) => s.preview);
  const isPro = plan === 'pro' || plan === 'team';
  const { slot, canAdd, canRemove } = sketchSlot({ sketchUrl, isPro, hasCalc: !!calcId, preview: !!preview });

  const addSketch = useCallback(async (file) => {
    if (!canAdd || uploading) return false;
    try {
      await uploadSketch(calcId, file);
      toast.success('Skizze hinzugefügt.');
      return true;
    } catch (err) {
      toast.error(err?.message || 'Skizze konnte nicht hochgeladen werden. Bitte später erneut versuchen.');
      return false;
    }
  }, [canAdd, uploading, uploadSketch, calcId]);

  const deleteSketch = useCallback(async () => {
    if (!canRemove || uploading) return;
    try {
      await removeSketch(calcId);
      toast.success('Skizze entfernt.');
    } catch (err) {
      toast.error(err?.message || 'Skizze konnte nicht entfernt werden.');
    }
  }, [canRemove, uploading, removeSketch, calcId]);

  return { calcId, sketchUrl, slot, canAdd, canRemove, uploading, addSketch, deleteSketch };
}

/**
 * Strg+V mit reinem Bild fügt die Skizze ein (überall auf der Rechenseite).
 * enabled=false z. B. auf der Handy-Ansicht, wo keine Skizze sichtbar ist.
 */
export function useSketchPaste({ enabled = true } = {}) {
  const { canAdd, addSketch } = useSketch();
  useEffect(() => {
    if (!enabled || !canAdd) return undefined;
    const onPaste = (e) => {
      const file = imageFromPaste(e);
      if (!file) return;
      e.preventDefault();
      addSketch(file);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [enabled, canAdd, addSketch]);
}
